// /sourcing/templates — 邮件模板子页
// v1.12 P0: 从主路由抽出来, 搬原 EmailTemplateGrouped 函数过来
// 复用: EmailTemplateCard + emailApi.listTemplates
// mobile first: 搜索 + 分类 chip 横滚
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { EmailTemplateCard } from '../components/EmailTemplateCard';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { Skeleton } from '../components/Skeleton';
import { Toast } from '../components/Toast';
import { Input } from '../components/ui/Input';
import { type EmailCategory, emailApi } from '../lib/api';

export const Route = createFileRoute('/sourcing/templates')({
  component: SourcingTemplatesPage,
});

const CATEGORY_LABELS: Record<EmailCategory | 'all', string> = {
  all: '全部',
  outreach: '主动开发',
  quote: '报价 / 寄样',
  retention: '客户维系',
};

const CATEGORY_ICONS: Record<EmailCategory | 'all', string> = {
  all: 'list',
  outreach: 'target',
  quote: 'circle-dollar-sign',
  retention: 'mail',
};

function SourcingTemplatesPage() {
  const [emailCategoryFilter, setEmailCategoryFilter] = useState<EmailCategory | 'all'>('all');
  const [emailSearch, setEmailSearch] = useState('');
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => emailApi.listTemplates(),
  });

  return (
    <div className="animate-fade-up">
      {/* 顶部返回条 */}
      <div className="mb-4 flex items-center gap-2 text-sm">
        <Link
          to="/sourcing"
          className="inline-flex items-center gap-1 text-ink-3 hover:text-accent min-h-[44px] px-2 -ml-2"
          aria-label="返回找客中心"
        >
          <Icon name="arrow-left" size="sm" aria-hidden />
          返回找客中心
        </Link>
      </div>

      <header className="mb-5">
        <h1 className="text-xl sm:text-2xl font-semibold text-ink leading-tight flex items-center gap-2">
          <Icon name="mail" size="md" className="text-accent" aria-hidden />
          邮件模板库
        </h1>
        <p className="text-xs sm:text-sm text-ink-3 mt-1 leading-relaxed">
          {data ? `${data.total} 条 · 8 keys × 3 langs` : '加载中…'} · 8 个拓客 SOP 模板 (冷开发 /
          跟进 / 报价 / 寄样 / 节日 / 流失挽回)
        </p>
      </header>

      {/* 搜索 + 分类 chip — mobile 横滚 */}
      <div className="mb-4 space-y-2">
        <Input
          value={emailSearch}
          onChange={(e) => setEmailSearch(e.target.value)}
          placeholder="搜索 key / 名称 / 描述…"
          leftIcon="search"
          size="md"
          block
          autoComplete="off"
          aria-label="搜索邮件模板"
        />
        <div className="-mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto">
          <div className="flex items-center gap-1.5 w-max sm:w-auto sm:flex-wrap">
            {(Object.keys(CATEGORY_LABELS) as Array<EmailCategory | 'all'>).map((k) => {
              const active = emailCategoryFilter === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setEmailCategoryFilter(k)}
                  className={`shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors min-h-[32px] ${
                    active
                      ? 'bg-accent text-white border-accent'
                      : 'bg-surface text-ink-2 border-line hover:border-accent hover:text-accent'
                  }`}
                  aria-pressed={active}
                >
                  <Icon name={CATEGORY_ICONS[k]} size="xs" aria-hidden />
                  {CATEGORY_LABELS[k]}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* 内容 */}
      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      )}

      {isError && (
        <EmptyState
          icon="alert-triangle"
          title="加载模板失败"
          description={(error as Error).message}
        />
      )}

      {data && (
        <EmailTemplateGrouped
          templates={data.templates}
          searchQuery={emailSearch}
          categoryFilter={emailCategoryFilter}
          onToast={(kind, msg) => setToast({ kind, msg })}
        />
      )}

      {toast && <Toast kind={toast.kind} message={toast.msg} onClose={() => setToast(null)} />}
    </div>
  );
}

// ============ 邮件模板分组渲染 (从主路由搬过来) ============
// 按 category 分组 (outreach / quote / retention) + 搜索过滤
function EmailTemplateGrouped({
  templates,
  searchQuery,
  categoryFilter,
  onToast,
}: {
  templates: import('../lib/api').EmailTemplate[];
  searchQuery: string;
  categoryFilter: EmailCategory | 'all';
  onToast: (kind: 'success' | 'error', msg: string) => void;
}) {
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return templates.filter((t) => {
      if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
      if (!q) return true;
      return (
        t.key.toLowerCase().includes(q) ||
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q)
      );
    });
  }, [templates, searchQuery, categoryFilter]);

  const grouped = useMemo(() => {
    const out: Record<EmailCategory, typeof filtered> = {
      outreach: [],
      quote: [],
      retention: [],
    };
    for (const t of filtered) out[t.category].push(t);
    return out;
  }, [filtered]);

  if (filtered.length === 0) {
    return (
      <EmptyState
        icon="search-x"
        title="没有匹配的模板"
        description="试试清除搜索或换其他 category"
      />
    );
  }

  const sections: Array<{ category: EmailCategory; title: string; icon: string }> = [
    { category: 'outreach', title: '主动开发', icon: 'cold outreach + follow-up' },
    { category: 'quote', title: '报价 / 寄样', icon: 'quote reply + PI + sample' },
    { category: 'retention', title: '客户维系', icon: 'holiday + winback' },
  ];

  return (
    <div className="space-y-5">
      {sections.map((s) => {
        const items = grouped[s.category];
        if (items.length === 0) return null;
        return (
          <div key={s.category}>
            <h2 className="text-sm font-semibold text-ink-2 mb-2 flex items-center gap-1.5">
              <span>{s.title}</span>
              <span className="text-ink-3 font-normal text-xs">({items.length} 个)</span>
              <span className="text-ink-3 font-normal text-[10px] ml-1">— {s.icon}</span>
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {items.map((tpl) => (
                <EmailTemplateCard key={tpl.key} template={tpl} onToast={onToast} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
